import {
  isFamilyOnlyRoleBindings,
  isTeamCaptainOnlyRoleBindings,
  isGlobalSuperadminForSession,
  loadMyGovernanceAccessBundleRemote,
} from "@tennis-matches/services/governance";
import { loadAppManifests } from "@tennis-matches/services/app-manifests";
import {
  areFeatureFlagsReadySync,
  isFeatureEnabledSync,
} from "@tennis-matches/services/feature-flags";
import { loadUserSettings } from "@tennis-matches/services/settings";
import { getSupabaseUser } from "@tennis-matches/services/supabase";
import { getEffectiveVisibleAppsForScope } from "./governance/canAccessApp.js";
import {
  canUsePreferredHomeApp,
  listMarketplaceHomeApps,
  normalizePreferredHomeApp,
  readStoredPreferredHomeAppSync,
  resolveHomeRoute,
} from "./home-routing.js";

/**
 * Home-Route nach Login (E-Mail/OAuth): Governance-Matrix + Manifeste, nicht Legacy-Rollen-Fallback allein.
 */
export async function resolvePostLoginHomeRouteAsync() {
  const [settingsRes, bundleRes, manifestsRes, authUser] = await Promise.all([
    loadUserSettings().catch(() => null),
    loadMyGovernanceAccessBundleRemote().catch(() => null),
    loadAppManifests().catch(() => ({ ok: false, manifests: [] })),
    getSupabaseUser().catch(() => null),
  ]);

  const roleBindings =
    bundleRes?.ok && Array.isArray(bundleRes.bindings)
      ? bundleRes.bindings
      : [];
  const entitlements =
    bundleRes?.ok && Array.isArray(bundleRes.entitlements)
      ? bundleRes.entitlements
      : [];
  const roleAppAccessRows =
    bundleRes?.ok && Array.isArray(bundleRes.roleAppAccessRows)
      ? bundleRes.roleAppAccessRows
      : [];
  const manifests =
    manifestsRes?.ok && Array.isArray(manifestsRes.manifests)
      ? manifestsRes.manifests
      : [];
  const manifestsLoaded = Boolean(manifestsRes?.ok);

  const featureEnabled = (appKey) =>
    !areFeatureFlagsReadySync() ||
    isFeatureEnabledSync(String(appKey || "").trim()) !== false;

  const tenantScopeIds = Array.from(
    new Set(
      roleBindings
        .filter((b) => String(b?.scope || "").toLowerCase() === "tenant")
        .map((b) => String(b?.scope_id || "").trim())
        .filter(Boolean),
    ),
  );

  const effectiveKeys = new Set();
  if (manifestsLoaded) {
    for (const sid of tenantScopeIds) {
      const effective = getEffectiveVisibleAppsForScope({
        scope: "tenant",
        scopeId: sid,
        appManifests: manifests,
        isManifestsLoaded: true,
        roleBindings,
        entitlements,
        roleAppAccessRows,
        isFeatureEnabled: featureEnabled,
        authUser,
      });
      for (const key of effective?.effectiveVisibleAppKeys || [])
        effectiveKeys.add(key);
    }

    if (isGlobalSuperadminForSession(roleBindings, authUser)) {
      const effectiveGlobal = getEffectiveVisibleAppsForScope({
        scope: "global",
        scopeId: "global",
        appManifests: manifests,
        isManifestsLoaded: true,
        roleBindings,
        entitlements,
        roleAppAccessRows,
        isFeatureEnabled: featureEnabled,
        authUser,
      });
      for (const key of effectiveGlobal?.effectiveVisibleAppKeys || [])
        effectiveKeys.add(key);
    }
  }

  const allowedAppKeys = listMarketplaceHomeApps(Array.from(effectiveKeys));

  const fromSettings = normalizePreferredHomeApp(
    settingsRes?.settings?.preferredHomeApp ||
      settingsRes?.settings?.preferred_home_app,
  );
  const fromSync = readStoredPreferredHomeAppSync();
  const preferredHomeApp = canUsePreferredHomeApp(fromSettings)
    ? fromSettings
    : fromSync;

  return resolveHomeRoute({
    preferredHomeApp,
    allowedAppKeys,
    isFamilyOnlyLogin: isFamilyOnlyRoleBindings(roleBindings),
    isTeamCaptainOnlyLogin: isTeamCaptainOnlyRoleBindings(roleBindings),
  });
}
